import { SharePointListClient, type ListItem } from '../SharePointListClient';

/**
 * 99.ABM_Contactos field shape. Column internal names kept verbatim because
 * they become OData `fields/xxx` paths. `NombreApellido_C` is computed in
 * PowerApps (Nombre + " " + Apellido); we mirror that on writes so the legacy
 * app keeps rendering a proper display name.
 */
export type ContactosFields = {
  Title?: string | null;
  NombreApellido_C?: string | null;
  Nombre_C?: string | null;
  Apellido_C?: string | null;
  Mail_C?: string | null;
  Celular_C?: string | null;
  CUIT_C?: string | null;
  Direccion_C?: string | null;
  DireccionCompleta_C?: string | null;
  CP_C?: string | null;
  Receptor_C?: string | null;
  Observaciones_C?: string | null;
  Referido_C?: string | null;
  RazonSocial_C?: string | null;
  Ciudad_C?: string | null;
  Altura_C?: string | null;
  Piso_C?: string | null;
  Depto_C?: string | null;
  Provincia_C?: string | null;
  Pais_C?: string | null;
  /** MX SAT Código Postal del receptor (5 dígitos). MX-only. */
  CodigoPostal_C?: string | null;
  /** MX SAT c_UsoCFDI code (e.g. G03, P01). MX-only. */
  UsoCFDI_C?: string | null;
  MedioDeLlegada_C?: string | null;
  Status_C?: 'Activo' | 'Inactivo' | string | null;
  UsuarioCreador_C?: string | null;
  /** AR: % mínimo de E-cheqs acreditado para despachar. Null = parámetro general. Sólo admin/administración. */
  PctMinimoEcheq_C?: string | number | null;
  /** AR: % de comisión de la vendedora en ventas a este cliente (vacío = regla general). */
  ComisionVendedor_C?: string | number | null;
  /** Cliente del Exterior (Factura E): código de país destino de ARCA. */
  PaisDestinoArca_C?: string | null;
  /** Cliente del Exterior: 'juridica' | 'fisica' | 'otro' (con el país define el CUIT-país). */
  TipoEntidadExterior_C?: string | null;
  /** Cliente del Exterior: número fiscal en su país (RUT, RFC…). */
  IdImpositivoExterior_C?: string | null;
};

export type ContactoItem = ListItem<ContactosFields>;

export const contactosClient = new SharePointListClient<ContactosFields>('ABM_Contactos');

/** Scans all active contactos. `Status_C` must be indexed in SharePoint. */
export async function listActiveContactos(): Promise<ContactoItem[]> {
  return contactosClient.listItems({
    filter: "fields/Status_C eq 'Activo'",
    top: 5000,
  });
}

export async function createContacto(
  fields: Partial<ContactosFields>,
  createdBy: string,
): Promise<ContactoItem> {
  const nombre = fields.Nombre_C?.trim() ?? '';
  const apellido = fields.Apellido_C?.trim() ?? '';
  // `Pais_C` is intentionally omitted — the server derives it from the
  // active country header. The previous hardcoded 'Argentina' silently
  // mislabeled MX contactos as Argentinian until 2026-05-26.
  return contactosClient.createItem({
    Title: '[sumar]',
    Status_C: 'Activo',
    UsuarioCreador_C: createdBy,
    ...fields,
    Nombre_C: nombre,
    Apellido_C: apellido,
    NombreApellido_C: `${nombre} ${apellido}`.trim(),
  });
}

export async function updateContacto(
  id: string,
  fields: Partial<ContactosFields>,
): Promise<ContactoItem> {
  const patch: Partial<ContactosFields> = { ...fields, Status_C: 'Activo' };
  if (fields.Nombre_C !== undefined || fields.Apellido_C !== undefined) {
    patch.NombreApellido_C = `${fields.Nombre_C ?? ''} ${fields.Apellido_C ?? ''}`.trim();
  }
  return contactosClient.updateItem(id, patch);
}

/** Soft-delete: mark Status_C = 'Inactivo'. Matches the legacy PowerApps behavior. */
export async function softDeleteContacto(id: string): Promise<ContactoItem> {
  return contactosClient.updateItem(id, { Status_C: 'Inactivo' });
}
